import React from 'react';
import i18next from 'i18next';
import { isEmpty } from 'lodash';
import { useLocalStateFields } from '@hooks-clean/index';
import { flattenObject } from '@utils-clean/flatten';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    ArrayOfObjectsV2,
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
// @ts-expect-error @types collides with node_modules/@types
import { FieldDefinitionFieldType, RowDataSourceTable } from '@types/graphql-global-types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatConfigTimeline(timeline: any) {
    if (timeline == null) {
        return {};
    }

    const startDate = timeline.StartDate as { $date: string } | undefined;
    const endDateFields = timeline.EndDateFields as string[] | undefined;
    const sortAscending = timeline.SortAscending as boolean | undefined;
    const milestoneDateFields = timeline.MilestoneDateFields as string[] | undefined;
    const panels = timeline.Panels as ({ _id: { $oid: string }; PlotTitle: string } | null)[] | undefined;

    const formattedStartDate =
        startDate?.$date != null ? stringDateWithoutTimeFromLocal(startDate.$date, 'DD-MMM-yyyy') : undefined;

    // endDateFields (string[]) needs to be mapped to an array of objects to work correctly with the arrayOfObjectsV2 system
    // The objects need a consist 'key' property between the old and new lists (index)
    const formattedEndDateFields =
        endDateFields != null ? endDateFields.map((value, index) => ({ index, value })) : undefined;

    const formattedSort =
        sortAscending != null
            ? i18next.t(
                  `admin:subjectConfig.subjectProfileDetailsConfig.timeline.sortLOV.${sortAscending ? 'asc' : 'desc'}`,
              )
            : undefined;

    const formattedMilestoneDateFields =
        milestoneDateFields != null ? milestoneDateFields.map((value, index) => ({ index, value })) : undefined;

    const formattedPanels = panels?.map((x, index) =>
        x != null
            ? {
                  id: x._id.$oid,
                  plotTitle: x.PlotTitle,
                  index,
              }
            : null,
    );

    const prefix = 'SubjectProfileDetailsConfigurationItem-Timeline';
    return {
        [`${prefix}-StartDate`]: formattedStartDate,
        [`${prefix}-EndDateFields`]: formattedEndDateFields,
        [`${prefix}-Sort`]: formattedSort,
        [`${prefix}-MilestoneDateFields`]: formattedMilestoneDateFields,
        [`${prefix}-ShowTagsAsMilestones`]: timeline.ShowTagsAsMilestones,
        [`${prefix}-Panels`]: formattedPanels,
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatConfig(config: any) {
    if (config == null) {
        return null;
    }

    const { Timeline, ...rest } = config;

    return {
        ...flattenObject({ SubjectProfileDetailsConfigurationItem: rest }, '-'),
        ...formatConfigTimeline(Timeline),
    };
}

type RecordData = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SubjectProfileDetailsConfigurationItem?: any;
    CfId: {
        $oid: string;
    };
    A: {
        $oid: string;
    };
    D: boolean;
    H: number;
    T: {
        $date: string;
    };
    AuditDescription?: string;
    AuditDescriptionMessage?: string;
};

type Props = {
    oldRecord: Partial<RecordData>;
    newRecord: Partial<RecordData>;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
};

const subjectDateFieldsFieldTypes = [FieldDefinitionFieldType.DATE, FieldDefinitionFieldType.PARTIAL_DATE];
const subjectDateFieldsDataTypes = [RowDataSourceTable.SUBJECT];

const SMSubjectProfileDetailsConfigRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: Props) => {
    const subjectDateFieldsLOV = useLocalStateFields({
        isCalculated: false,
        isHidden: false,
        fieldTypes: subjectDateFieldsFieldTypes,
        dataTypes: subjectDateFieldsDataTypes,
    });

    const subjectDateFieldsLOVMap = new Map(subjectDateFieldsLOV.map(({ key, name }) => [key, name]));

    if (isEmpty(oldRecord) && isEmpty(newRecord)) return null;

    const { SubjectProfileDetailsConfigurationItem: oldConfig, ...oldRecordRest } = oldRecord;
    const { SubjectProfileDetailsConfigurationItem: newConfig, ...newRecordRest } = newRecord;

    // Lots of ID fields to hide
    const hiddenFields: string[] = ['CfId', 'AuditDescription', 'AuditDescriptionMessage'];

    const refactoredOldRecord = {
        ...oldRecordRest,
        ...removePropertiesFromObject(oldRecordRest, hiddenFields),
        ...formatConfig(oldConfig),
    } as Record<string, unknown>;

    const refactoredNewRecord = {
        ...newRecordRest,
        ...removePropertiesFromObject(newRecordRest, hiddenFields),
        ...formatConfig(newConfig),
    } as Record<string, unknown>;

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2: {
                'SubjectProfileDetailsConfigurationItem-Timeline-EndDateFields': {
                    valueResolver: (item, index) => {
                        if (!(item instanceof Object) || !('value' in item) || typeof item.value !== 'string') {
                            return null;
                        }
                        const { value } = item;
                        return `${index + 1}: ${subjectDateFieldsLOVMap.get(value) ?? value}`;
                    },
                    // This is actually the key/id field, not the field that gets compared...
                    compareField: 'index',
                    valueComparer: (oldObject, newObject) => {
                        // Try to get value out of object, if defined
                        const oldValue = oldObject instanceof Object && 'value' in oldObject ? oldObject.value : null;
                        const newValue = newObject instanceof Object && 'value' in newObject ? newObject.value : null;

                        return oldValue !== newValue; // True means display. False means don't...
                    },
                },
                'SubjectProfileDetailsConfigurationItem-Timeline-MilestoneDateFields': {
                    valueResolver: (item, index) => {
                        if (!(item instanceof Object) || !('value' in item) || typeof item.value !== 'string') {
                            return null;
                        }
                        const { value } = item;
                        return `${index + 1}: ${subjectDateFieldsLOVMap.get(value) ?? value}`;
                    },
                    compareField: 'index',
                    valueComparer: (oldObject, newObject) => {
                        const oldValue = oldObject instanceof Object && 'value' in oldObject ? oldObject.value : null;
                        const newValue = newObject instanceof Object && 'value' in newObject ? newObject.value : null;

                        return oldValue !== newValue;
                    },
                },
                'SubjectProfileDetailsConfigurationItem-Timeline-Panels': {
                    valueResolver: (item, index) => {
                        if (!(item instanceof Object) || !('plotTitle' in item) || typeof item.plotTitle !== 'string') {
                            return null;
                        }
                        const { plotTitle } = item;
                        return `${index + 1}: ${plotTitle}`;
                    },
                    compareField: 'index',
                    // See if panel id has changed at a given index
                    valueComparer: (oldObject, newObject) => {
                        const oldId = oldObject instanceof Object && 'id' in oldObject ? oldObject.id : null;
                        const newId = newObject instanceof Object && 'id' in newObject ? newObject.id : null;

                        return oldId !== newId;
                    },
                },
            } satisfies ArrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { SMSubjectProfileDetailsConfigRecordChangeGrid };
